import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, HelpCircle, Upload, Eye, Clock } from 'lucide-react';
import { QuestionTypeSelector, QuestionCard, TestCard } from '@/components/admin';

const TestManagement = () => {
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [showCSVUploadDialog, setShowCSVUploadDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTest, setPreviewTest] = useState(null);
  const [csvFile, setCSVFile] = useState(null);
  const [csvUploadTarget, setCSVUploadTarget] = useState({ chapter_id: '', test_ids: [] });
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedTestFilter, setSelectedTestFilter] = useState('all'); // 'all' or test_id
  
  const [loading, setLoading] = useState(false);
  
  const [testForm, setTestForm] = useState({
    id: null,
    chapter_id: '',
    title: '',
    order: 1,
    time_limit: 1200,
    total_questions: 20,
    passing_marks: 10,
    randomize_questions: false,
    question_ids: []
  });
  
  const [questionForm, setQuestionForm] = useState({
    id: null,
    chapter_id: '',
    question_type: 'simple',  // 'simple' or 'combo'
    question_text: '',
    // Simple MCQ fields
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'a',
    // Combo MCQ fields
    stmt_a: '',
    stmt_b: '',
    stmt_c: '',
    stmt_d: '',
    combo_option_a: '',
    combo_option_b: '',
    combo_option_c: '',
    combo_option_d: '',
    correct_combo_option: 'a',
    // Common
    explanation: '',
    test_ids: []
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) fetchSubjects();
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedSubject) fetchChapters();
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedChapter) {
      fetchTests();
      fetchQuestions();
    }
  }, [selectedChapter]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`/subjects/${selectedCourse}`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`/chapters/${selectedSubject}`);
      setChapters(response.data);
    } catch (error) {
      console.error('Failed to fetch chapters:', error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await axios.get(`/mock-tests/${selectedChapter}`);
      setTests(response.data);
    } catch (error) {
      console.error('Failed to fetch tests:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`/admin/questions/${selectedChapter}`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    }
  };

  const handleSaveTest = async (e) => {
    e.preventDefault();
    try {
      if (testForm.id) {
        await axios.put(`/admin/mock-tests/${testForm.id}`, testForm);
        toast.success('Test updated');
      } else {
        await axios.post('/admin/mock-tests', testForm);
        toast.success('Test created');
      }
      setShowTestDialog(false);
      setTestForm({ id: null, chapter_id: selectedChapter, title: '', order: 1, time_limit: 1200, total_questions: 20, passing_marks: 10, randomize_questions: false, question_ids: [] });
      fetchTests();
    } catch (error) {
      toast.error('Failed to save test');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Delete this test?')) return;
    try {
      await axios.delete(`/admin/mock-tests/${testId}`);
      toast.success('Test deleted');
      fetchTests();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    try {
      if (questionForm.id) {
        await axios.put(`/admin/questions/${questionForm.id}`, questionForm);
        toast.success('Question updated');
      } else {
        await axios.post('/admin/questions', questionForm);
        toast.success('Question added');
      }
      setShowQuestionDialog(false);
      setQuestionForm({ 
        id: null, 
        chapter_id: selectedChapter, 
        question_type: 'simple',
        question_text: '', 
        option_a: '', 
        option_b: '', 
        option_c: '', 
        option_d: '', 
        correct_answer: 'a',
        stmt_a: '',
        stmt_b: '',
        stmt_c: '',
        stmt_d: '',
        combo_option_a: '',
        combo_option_b: '',
        combo_option_c: '',
        combo_option_d: '',
        correct_combo_option: 'a',
        explanation: '' 
      });
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`/admin/questions/${questionId}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleBulkDeleteQuestions = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('No questions selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedQuestions.length} selected question(s)?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/admin/questions/bulk-delete', 
        { question_ids: selectedQuestions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedQuestions.length} question(s) deleted successfully`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete questions');
    }
  };

  const handleSelectAllQuestions = (checked) => {
    if (checked) {
      setSelectedQuestions(questions.map(q => q.id));
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleSelectQuestion = (questionId, checked) => {
    if (checked) {
      setSelectedQuestions([...selectedQuestions, questionId]);
    } else {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    }
  };

  const handleCSVUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    if (!selectedChapter) {
      toast.error('Please select a chapter first');
      return;
    }

    setLoading(true);
    
    try {
      const text = await csvFile.text();
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        toast.error('CSV file is empty');
        setLoading(false);
        return;
      }
      
      // Skip header if present (check if first line contains 'question')
      const startIndex = lines[0].toLowerCase().includes('question') ? 1 : 0;
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i];
        if (!line) continue;
        
        // Simple CSV parsing - split by comma
        // For quoted values, use a proper CSV parser approach
        const parts = [];
        let current = '';
        let inQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            parts.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        parts.push(current.trim());
        
        // Remove quotes from values
        const cleanValues = parts.map(v => v.replace(/^"|"$/g, ''));
        
        if (cleanValues.length < 6) {
          errors.push(`Line ${i + 1}: Not enough columns (found ${cleanValues.length}, need at least 6 for simple MCQ or 11 for combo MCQ)`);
          errorCount++;
          continue;
        }
        
        // Auto-detect question type based on number of columns
        // Simple MCQ: 7 columns (question, opt_a, opt_b, opt_c, opt_d, correct, explanation)
        // Combo MCQ: 11 columns (question, stmt_a, stmt_b, stmt_c, stmt_d, combo_opt_a, combo_opt_b, combo_opt_c, combo_opt_d, correct, explanation)
        
        let questionData;
        
        if (cleanValues.length >= 10) {
          // Combo MCQ format
          const [question_text, stmt_a, stmt_b, stmt_c, stmt_d, combo_option_a, combo_option_b, combo_option_c, combo_option_d, correct_combo_option, explanation] = cleanValues;
          
          // Validate
          if (!question_text || !stmt_a || !stmt_b || !stmt_c || !stmt_d || 
              !combo_option_a || !combo_option_b || !combo_option_c || !combo_option_d || !correct_combo_option) {
            errors.push(`Line ${i + 1}: Missing required fields for combo MCQ`);
            errorCount++;
            continue;
          }
          
          const correctAnswerLower = correct_combo_option.toLowerCase().trim();
          if (!['a', 'b', 'c', 'd'].includes(correctAnswerLower)) {
            errors.push(`Line ${i + 1}: Invalid correct_combo_option "${correct_combo_option}" (must be a, b, c, or d)`);
            errorCount++;
            continue;
          }
          
          questionData = {
            chapter_id: selectedChapter,
            question_type: 'combo',
            question_text: question_text.trim(),
            stmt_a: stmt_a.trim(),
            stmt_b: stmt_b.trim(),
            stmt_c: stmt_c.trim(),
            stmt_d: stmt_d.trim(),
            combo_option_a: combo_option_a.trim(),
            combo_option_b: combo_option_b.trim(),
            combo_option_c: combo_option_c.trim(),
            combo_option_d: combo_option_d.trim(),
            correct_combo_option: correctAnswerLower,
            explanation: (explanation || '').trim(),
            difficulty: 'medium',
            test_ids: csvUploadTarget.test_ids || []
          };
        } else {
          // Simple MCQ format
          const [question_text, option_a, option_b, option_c, option_d, correct_answer, explanation] = cleanValues;
          
          // Validate
          if (!question_text || !option_a || !option_b || !option_c || !option_d || !correct_answer) {
            errors.push(`Line ${i + 1}: Missing required fields for simple MCQ`);
            errorCount++;
            continue;
          }
          
          const correctAnswerLower = correct_answer.toLowerCase().trim();
          if (!['a', 'b', 'c', 'd'].includes(correctAnswerLower)) {
            errors.push(`Line ${i + 1}: Invalid correct_answer "${correct_answer}" (must be a, b, c, or d)`);
            errorCount++;
            continue;
          }
          
          questionData = {
            chapter_id: selectedChapter,
            question_type: 'simple',
            question_text: question_text.trim(),
            option_a: option_a.trim(),
            option_b: option_b.trim(),
            option_c: option_c.trim(),
            option_d: option_d.trim(),
            correct_answer: correctAnswerLower,
            explanation: (explanation || '').trim(),
            difficulty: 'medium',
            test_ids: csvUploadTarget.test_ids || []
          };
        }
        
        try {
          const response = await axios.post('/admin/questions', questionData);
          
          successCount++;
        } catch (error) {
          console.error(`Error saving question ${i + 1}:`, error);
          errors.push(`Line ${i + 1}: ${error.response?.data?.detail || 'Failed to save'}`);
          errorCount++;
        }
      }
      
      setShowCSVUploadDialog(false);
      setCSVFile(null);
      setCSVUploadTarget({ chapter_id: '', test_ids: [] });
      fetchQuestions();
      fetchTests(); // Refresh tests to show updated question counts
      setLoading(false);
      
      // Show results
      if (successCount > 0) {
        const testAssignmentMsg = csvUploadTarget.test_ids?.length > 0 
          ? ` and assigned to ${csvUploadTarget.test_ids.length} test(s)` 
          : '';
        toast.success(`✓ Successfully uploaded ${successCount} question${successCount > 1 ? 's' : ''}${testAssignmentMsg}${errorCount > 0 ? ` (${errorCount} skipped)` : ''}`);
      }
      
      if (errors.length > 0 && errors.length <= 5) {
        // Show first few errors
        errors.slice(0, 3).forEach(err => toast.error(err, { duration: 5000 }));
      } else if (errors.length > 5) {
        toast.error(`${errorCount} errors occurred. Check console for details.`);
        console.error('CSV Upload Errors:', errors);
      }
      
      if (successCount === 0 && errorCount > 0) {
        toast.error('No questions were uploaded. Please check CSV format.');
      }
      
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error(`Failed to process CSV: ${error.message}`);
      setLoading(false);
    }
  };


  const handlePreviewTest = async (test) => {
    try {
      // Fetch all questions for this test
      const testQuestions = questions.filter(q => test.question_ids?.includes(q.id));
      setPreviewTest({
        ...test,
        questions: testQuestions
      });
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load test preview');
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Tests & Questions Management</h2>
        <p className="text-gray-600 text-sm sm:text-base">Select a chapter to manage its tests and questions</p>
      </div>

      {/* Selection Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Course</label>
              <Select value={selectedCourse || undefined} onValueChange={(value) => {
                setSelectedCourse(value);
                setSelectedSubject('');
                setSelectedChapter('');
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Subject</label>
              <Select value={selectedSubject || undefined} onValueChange={(value) => {
                setSelectedSubject(value);
                setSelectedChapter('');
              }} disabled={!selectedCourse}>
                <SelectTrigger>
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Chapter</label>
              <Select value={selectedChapter || undefined} onValueChange={setSelectedChapter} disabled={!selectedSubject}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select chapter" className="truncate" />
                </SelectTrigger>
                <SelectContent>
                  {chapters.map((chapter) => (
                    <SelectItem key={chapter.id} value={chapter.id} className="truncate">{chapter.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedChapter && (
        <Tabs defaultValue="tests" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tests">Mock Tests</TabsTrigger>
            <TabsTrigger value="questions">Questions</TabsTrigger>
          </TabsList>

          {/* Tests Tab */}
          <TabsContent value="tests" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => {
                setTestForm({ id: null, chapter_id: selectedChapter, title: '', order: tests.length + 1, time_limit: 1200, total_questions: 20, passing_marks: 10, randomize_questions: false, question_ids: [] });
                setShowTestDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" /> Add Test
              </Button>
            </div>

            <div className="space-y-3">
              {tests.map((test) => (
                <Card key={test.id}>
                  <CardContent className="pt-4 md:pt-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <h4 className="font-semibold text-base md:text-lg">{test.order}. {test.title}</h4>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mt-3 text-xs md:text-sm">
                          <div className="flex flex-col">
                            <span className="text-gray-500">Questions</span>
                            <span className="font-semibold">{test.total_questions}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-gray-500">Time</span>
                            <span className="font-semibold">{test.time_limit / 60} mins</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-gray-500">Passing</span>
                            <span className="font-semibold">{test.passing_marks} marks</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-gray-500">Randomize</span>
                            <span className="font-semibold">{test.randomize_questions ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handlePreviewTest(test)} title="Preview Test">
                          <FileText className="w-4 h-4 mr-1" /> Preview
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setTestForm(test);
                          setShowTestDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteTest(test.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {tests.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No tests yet. Create your first test!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {selectedQuestions.length > 0 && (
                  <Button 
                    variant="destructive"
                    onClick={handleBulkDeleteQuestions}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedQuestions.length})
                  </Button>
                )}
              </div>
              <div className="flex gap-3 flex-wrap">
                {/* Test Filter Dropdown */}
                <select
                  value={selectedTestFilter}
                  onChange={(e) => {
                    setSelectedTestFilter(e.target.value);
                    setSelectedQuestions([]); // Clear selection when filter changes
                  }}
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  <option value="all">All Questions</option>
                  {tests.map((test) => (
                    <option key={test.id} value={test.id}>
                      Test {test.order}: {test.title} ({test.total_questions} questions)
                    </option>
                  ))}
                </select>

                <Button 
                  variant="outline"
                  onClick={() => {
                    setCSVUploadTarget({ chapter_id: selectedChapter, test_ids: [] });
                    setShowCSVUploadDialog(true);
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload CSV
                </Button>
                <Button onClick={() => {
                setQuestionForm({ 
                  id: null, 
                  chapter_id: selectedChapter, 
                  question_type: 'simple',
                  question_text: '', 
                  option_a: '', 
                  option_b: '', 
                  option_c: '', 
                  option_d: '', 
                  correct_answer: 'a',
                  stmt_a: '',
                  stmt_b: '',
                  stmt_c: '',
                  stmt_d: '',
                  combo_option_a: '',
                  combo_option_b: '',
                  combo_option_c: '',
                  combo_option_d: '',
                  correct_combo_option: 'a',
                  explanation: '', 
                  test_ids: [] 
                });
                setShowQuestionDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" /> Add Question
              </Button>
              </div>
            </div>

            {/* Select All Checkbox */}
            {(() => {
              const filteredQuestions = selectedTestFilter === 'all' 
                ? questions 
                : questions.filter(q => {
                    const selectedTest = tests.find(t => t.id === selectedTestFilter);
                    return selectedTest?.question_ids?.includes(q.id);
                  });
              
              if (filteredQuestions.length === 0) return null;
              
              return (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                  <Checkbox
                    id="select-all-questions"
                    checked={filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestions.includes(q.id))}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        const newSelections = [...new Set([...selectedQuestions, ...filteredQuestions.map(q => q.id)])];
                        setSelectedQuestions(newSelections);
                      } else {
                        const filteredIds = filteredQuestions.map(q => q.id);
                        setSelectedQuestions(selectedQuestions.filter(id => !filteredIds.includes(id)));
                      }
                    }}
                  />
                  <label htmlFor="select-all-questions" className="text-sm font-medium cursor-pointer">
                    Select All {selectedTestFilter !== 'all' ? 'Filtered' : ''} ({filteredQuestions.length})
                  </label>
                </div>
              );
            })()}

            <div className="space-y-3">
              {(() => {
                // Filter questions based on selected test
                const filteredQuestions = selectedTestFilter === 'all' 
                  ? questions 
                  : questions.filter(q => {
                      const selectedTest = tests.find(t => t.id === selectedTestFilter);
                      return selectedTest?.question_ids?.includes(q.id);
                    });
                
                return filteredQuestions.map((q, index) => (
                  <QuestionCard 
                    key={q.id}
                    question={q}
                    index={index}
                    isSelected={selectedQuestions.includes(q.id)}
                    onSelectChange={(checked) => handleSelectQuestion(q.id, checked)}
                    onEdit={() => {
                      const assignedTestIds = tests
                        .filter(test => test.question_ids?.includes(q.id))
                        .map(test => test.id);
                      setQuestionForm({
                        ...q,
                        test_ids: assignedTestIds
                      });
                      setShowQuestionDialog(true);
                    }}
                    onDelete={() => handleDeleteQuestion(q.id)}
                  />
                ));
              })()}
              {(() => {
                const filteredQuestions = selectedTestFilter === 'all' 
                  ? questions 
                  : questions.filter(q => {
                      const selectedTest = tests.find(t => t.id === selectedTestFilter);
                      return selectedTest?.question_ids?.includes(q.id);
                    });
                
                if (filteredQuestions.length === 0) {
                  return (
                    <Card>
                      <CardContent className="py-12 text-center text-gray-500">
                        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>
                          {selectedTestFilter === 'all' 
                            ? 'No questions yet. Add your first question!' 
                            : `No questions found for the selected test.`
                          }
                        </p>
                      </CardContent>
                    </Card>
                  );
                }
                return null;
              })()}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{testForm.id ? 'Edit Test' : 'Create Test'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTest} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Test Title</label>
              <Input value={testForm.title} onChange={(e) => setTestForm({ ...testForm, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Order</label>
                <Input type="number" value={testForm.order} onChange={(e) => setTestForm({ ...testForm, order: parseInt(e.target.value) })} min="1" required />
              </div>
              <div>
                <label className="text-sm font-medium">Total Questions</label>
                <Input type="number" value={testForm.total_questions} onChange={(e) => setTestForm({ ...testForm, total_questions: parseInt(e.target.value) })} min="1" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Time Limit (seconds)</label>
                <Input type="number" value={testForm.time_limit} onChange={(e) => setTestForm({ ...testForm, time_limit: parseInt(e.target.value) })} min="60" required />
                <p className="text-xs text-gray-500 mt-1">{(testForm.time_limit / 60).toFixed(1)} minutes</p>
              </div>
              <div>
                <label className="text-sm font-medium">Passing Marks</label>
                <Input type="number" value={testForm.passing_marks} onChange={(e) => setTestForm({ ...testForm, passing_marks: parseFloat(e.target.value) })} step="0.5" required />
              </div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Questions are automatically assigned to this test. All questions created in this chapter will be included. You can manage questions in the "Questions" tab.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={testForm.randomize_questions} onCheckedChange={(checked) => setTestForm({ ...testForm, randomize_questions: checked })} />
              <label className="text-sm font-medium">Randomize Question Order</label>
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
              <Button type="submit">Save Test</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{questionForm.id ? 'Edit Question' : 'Add Question'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveQuestion} className="space-y-4">
            {/* Question Type Selector */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <label className="text-sm font-medium mb-2 block">Question Type</label>
              <Select 
                value={questionForm.question_type || 'simple'} 
                onValueChange={(value) => setQuestionForm({ ...questionForm, question_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Simple MCQ (4 options)</SelectItem>
                  <SelectItem value="combo">Statement-Combination MCQ (Statements + Combinations)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-600 mt-1">
                {questionForm.question_type === 'combo' 
                  ? 'Create statements (A, B, C, D) and combination options' 
                  : 'Standard multiple choice with 4 options'}
              </p>
            </div>
            
            {questionForm.id && (
              <div>
                <label className="text-sm font-medium mb-2 block">Reassign to Chapter</label>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={selectedCourse || undefined} onValueChange={(value) => {
                    setSelectedCourse(value);
                    setSelectedSubject('');
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedSubject || undefined} onValueChange={setSelectedSubject} disabled={!selectedCourse}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((subject) => (
                        <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select 
                  value={questionForm.chapter_id || undefined} 
                  onValueChange={(value) => setQuestionForm({ ...questionForm, chapter_id: value })} 
                  disabled={!selectedSubject}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select chapter" />
                  </SelectTrigger>
                  <SelectContent>
                    {chapters.map((chapter) => (
                      <SelectItem key={chapter.id} value={chapter.id}>{chapter.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Change chapter to reassign this question to different tests</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Question Text / Stem</label>
              <Textarea value={questionForm.question_text} onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })} rows={3} required />
            </div>
            
            {/* Conditional fields based on question type */}
            {questionForm.question_type === 'combo' ? (
              /* Statement-Combination MCQ Fields */
              <>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-sm mb-3">Statements (will appear as A, B, C, D)</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Statement A</label>
                      <Textarea 
                        value={questionForm.stmt_a} 
                        onChange={(e) => setQuestionForm({ ...questionForm, stmt_a: e.target.value })} 
                        rows={2} 
                        required 
                        placeholder="First statement"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Statement B</label>
                      <Textarea 
                        value={questionForm.stmt_b} 
                        onChange={(e) => setQuestionForm({ ...questionForm, stmt_b: e.target.value })} 
                        rows={2} 
                        required 
                        placeholder="Second statement"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Statement C</label>
                      <Textarea 
                        value={questionForm.stmt_c} 
                        onChange={(e) => setQuestionForm({ ...questionForm, stmt_c: e.target.value })} 
                        rows={2} 
                        required 
                        placeholder="Third statement"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Statement D</label>
                      <Textarea 
                        value={questionForm.stmt_d} 
                        onChange={(e) => setQuestionForm({ ...questionForm, stmt_d: e.target.value })} 
                        rows={2} 
                        required 
                        placeholder="Fourth statement"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-sm mb-3">Combination Options (will appear as a, b, c, d)</h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="text-sm font-medium">Option (a)</label>
                      <Input 
                        value={questionForm.combo_option_a} 
                        onChange={(e) => setQuestionForm({ ...questionForm, combo_option_a: e.target.value })} 
                        required 
                        placeholder="e.g., Both A and B are true"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Option (b)</label>
                      <Input 
                        value={questionForm.combo_option_b} 
                        onChange={(e) => setQuestionForm({ ...questionForm, combo_option_b: e.target.value })} 
                        required 
                        placeholder="e.g., Both B and C are true"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Option (c)</label>
                      <Input 
                        value={questionForm.combo_option_c} 
                        onChange={(e) => setQuestionForm({ ...questionForm, combo_option_c: e.target.value })} 
                        required 
                        placeholder="e.g., Both C and D are true"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Option (d)</label>
                      <Input 
                        value={questionForm.combo_option_d} 
                        onChange={(e) => setQuestionForm({ ...questionForm, combo_option_d: e.target.value })} 
                        required 
                        placeholder="e.g., All A, B, C, and D are true"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Correct Combination Option</label>
                  <Select value={questionForm.correct_combo_option || 'a'} onValueChange={(value) => setQuestionForm({ ...questionForm, correct_combo_option: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select correct option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a">Option (a)</SelectItem>
                      <SelectItem value="b">Option (b)</SelectItem>
                      <SelectItem value="c">Option (c)</SelectItem>
                      <SelectItem value="d">Option (d)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              /* Simple MCQ Fields */
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Option A</label>
                    <Input value={questionForm.option_a} onChange={(e) => setQuestionForm({ ...questionForm, option_a: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Option B</label>
                    <Input value={questionForm.option_b} onChange={(e) => setQuestionForm({ ...questionForm, option_b: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Option C</label>
                    <Input value={questionForm.option_c} onChange={(e) => setQuestionForm({ ...questionForm, option_c: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Option D</label>
                    <Input value={questionForm.option_d} onChange={(e) => setQuestionForm({ ...questionForm, option_d: e.target.value })} required />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Correct Answer</label>
                  <Select value={questionForm.correct_answer || 'a'} onValueChange={(value) => setQuestionForm({ ...questionForm, correct_answer: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select correct answer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a">Option A</SelectItem>
                      <SelectItem value="b">Option B</SelectItem>
                      <SelectItem value="c">Option C</SelectItem>
                      <SelectItem value="d">Option D</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium">Explanation (optional)</label>
              <Textarea value={questionForm.explanation || ''} onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })} rows={2} placeholder="Provide explanation or reference for the correct answer" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Assign to Mock Tests</label>
              <div className="border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                {tests.length > 0 ? (
                  <>
                    <div className="flex items-center space-x-2 mb-3">
                      <Checkbox
                        id="select-all-tests"
                        checked={questionForm.test_ids?.length === tests.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setQuestionForm({ ...questionForm, test_ids: tests.map(t => t.id) });
                          } else {
                            setQuestionForm({ ...questionForm, test_ids: [] });
                          }
                        }}
                      />
                      <label htmlFor="select-all-tests" className="text-sm font-medium cursor-pointer">
                        Select All Tests
                      </label>
                    </div>
                    {tests.map((test) => (
                      <div key={test.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`test-${test.id}`}
                          checked={questionForm.test_ids?.includes(test.id)}
                          onCheckedChange={(checked) => {
                            const currentTestIds = questionForm.test_ids || [];
                            if (checked) {
                              setQuestionForm({ ...questionForm, test_ids: [...currentTestIds, test.id] });
                            } else {
                              setQuestionForm({ ...questionForm, test_ids: currentTestIds.filter(id => id !== test.id) });
                            }
                          }}
                        />
                        <label htmlFor={`test-${test.id}`} className="text-sm cursor-pointer">
                          {test.order}. {test.title}
                        </label>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No tests available in this chapter. Create a test first.</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {questionForm.test_ids?.length > 0 
                  ? `This question will be added to ${questionForm.test_ids.length} test(s)`
                  : 'Select at least one test to assign this question'}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowQuestionDialog(false)}>Cancel</Button>
              <Button type="submit">Save Question</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Test Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Test Preview: {previewTest?.title}</DialogTitle>
          </DialogHeader>
          {previewTest && (
            <div className="space-y-6">
              {/* Test Info */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Total Questions</p>
                      <p className="font-semibold text-lg">{previewTest.total_questions}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Time Limit</p>
                      <p className="font-semibold text-lg">{previewTest.time_limit / 60} mins</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Passing Marks</p>
                      <p className="font-semibold text-lg">{previewTest.passing_marks}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Randomize</p>
                      <p className="font-semibold text-lg">{previewTest.randomize_questions ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Questions Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Questions ({previewTest.questions?.length || 0})</h3>
                {previewTest.questions && previewTest.questions.length > 0 ? (
                  previewTest.questions.map((q, index) => (
                    <Card key={q.id}>
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          <h4 className="font-semibold">Q{index + 1}. {q.question_text}</h4>
                          
                          {q.question_type === 'combo' ? (
                            /* Combo MCQ Preview */
                            <>
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r ml-4">
                                <p className="text-xs font-semibold text-blue-900 mb-2">Statements:</p>
                                <div className="space-y-1.5 text-sm">
                                  <div><strong>A.</strong> {q.stmt_a}</div>
                                  <div><strong>B.</strong> {q.stmt_b}</div>
                                  <div><strong>C.</strong> {q.stmt_c}</div>
                                  <div><strong>D.</strong> {q.stmt_d}</div>
                                </div>
                              </div>
                              <div className="ml-4">
                                <p className="text-xs font-semibold text-gray-700 mb-2">Combination Options:</p>
                                <div className="space-y-2">
                                  <div className={`p-2 rounded ${q.correct_combo_option === 'a' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                    <span className="font-medium">(a)</span> {q.combo_option_a}
                                    {q.correct_combo_option === 'a' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                                  </div>
                                  <div className={`p-2 rounded ${q.correct_combo_option === 'b' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                    <span className="font-medium">(b)</span> {q.combo_option_b}
                                    {q.correct_combo_option === 'b' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                                  </div>
                                  <div className={`p-2 rounded ${q.correct_combo_option === 'c' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                    <span className="font-medium">(c)</span> {q.combo_option_c}
                                    {q.correct_combo_option === 'c' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                                  </div>
                                  <div className={`p-2 rounded ${q.correct_combo_option === 'd' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                    <span className="font-medium">(d)</span> {q.combo_option_d}
                                    {q.correct_combo_option === 'd' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            /* Simple MCQ Preview */
                            <div className="grid grid-cols-2 gap-2 ml-4">
                              <div className={`p-2 rounded ${q.correct_answer === 'a' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                <span className="font-medium">A.</span> {q.option_a}
                                {q.correct_answer === 'a' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                              </div>
                              <div className={`p-2 rounded ${q.correct_answer === 'b' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                <span className="font-medium">B.</span> {q.option_b}
                                {q.correct_answer === 'b' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                              </div>
                              <div className={`p-2 rounded ${q.correct_answer === 'c' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                <span className="font-medium">C.</span> {q.option_c}
                                {q.correct_answer === 'c' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                              </div>
                              <div className={`p-2 rounded ${q.correct_answer === 'd' ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                                <span className="font-medium">D.</span> {q.option_d}
                                {q.correct_answer === 'd' && <span className="ml-2 text-green-600 text-xs">✓ Correct</span>}
                              </div>
                            </div>
                          )}
                          {q.explanation && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm"><span className="font-semibold">Explanation:</span> {q.explanation}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-gray-500">
                      <p>No questions assigned to this test yet.</p>
                      <p className="text-sm mt-2">Add question IDs to the test or create new questions.</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setShowPreviewDialog(false)}>Close Preview</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* CSV Upload Dialog */}
      <Dialog open={showCSVUploadDialog} onOpenChange={setShowCSVUploadDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Questions via CSV</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCSVUpload} className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">Question Types:</h4>
                
                <div className="space-y-2">
                  {/* Simple MCQ */}
                  <div className="border-l-4 border-green-500 pl-2">
                    <p className="font-semibold text-xs text-gray-900 mb-1">1. Simple MCQ</p>
                    <div className="p-2 bg-gray-50 rounded text-xs">
                      <strong>Example:</strong><br/>
                      <code className="text-xs">What is 2+2?,2,3,4,5,c,4 is the sum</code>
                    </div>
                  </div>
                  
                  {/* Combo MCQ */}
                  <div className="border-l-4 border-purple-500 pl-2">
                    <p className="font-semibold text-xs text-gray-900 mb-1">2. Statement-Combination MCQ</p>
                    <div className="p-2 bg-gray-50 rounded text-xs">
                      <strong>Example:</strong><br/>
                      <code className="text-xs">Which are true?,Statement A,Statement B,Statement C,Statement D,Both A and B,Both B and C,Both C and D,All are true,d</code>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-2">
                  Target Chapter: <span className="text-blue-600 font-semibold">
                    {chapters.find(c => c.id === selectedChapter)?.name || 'None selected'}
                  </span>
                </label>
                {!selectedChapter && (
                  <p className="text-sm text-red-600">⚠️ Please select a chapter from the dropdown above before uploading</p>
                )}
              </div>

              {/* Assign to Tests */}
              {selectedChapter && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <label className="text-sm font-medium block mb-3">
                    Assign uploaded questions to tests (optional):
                  </label>
                  {tests.length === 0 ? (
                    <p className="text-sm text-gray-600 italic">
                      No tests found in this chapter. Questions will be created without test assignment.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        <div className="flex items-center gap-2 p-2 bg-white rounded border">
                          <Checkbox
                            checked={csvUploadTarget.test_ids?.length === tests.length && tests.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setCSVUploadTarget({ ...csvUploadTarget, test_ids: tests.map(t => t.id) });
                              } else {
                                setCSVUploadTarget({ ...csvUploadTarget, test_ids: [] });
                              }
                            }}
                          />
                          <label className="text-sm font-semibold cursor-pointer">
                            Select All Tests
                          </label>
                        </div>
                        {tests.map((test) => (
                          <div key={test.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                            <Checkbox
                              checked={csvUploadTarget.test_ids?.includes(test.id)}
                              onCheckedChange={(checked) => {
                                const currentTestIds = csvUploadTarget.test_ids || [];
                                if (checked) {
                                  setCSVUploadTarget({ ...csvUploadTarget, test_ids: [...currentTestIds, test.id] });
                                } else {
                                  setCSVUploadTarget({ ...csvUploadTarget, test_ids: currentTestIds.filter(id => id !== test.id) });
                                }
                              }}
                            />
                            <label className="text-sm cursor-pointer flex-1">
                              {test.title}
                            </label>
                          </div>
                        ))}
                      </div>
                      {csvUploadTarget.test_ids?.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">
                          ✓ Questions will be assigned to {csvUploadTarget.test_ids.length} test(s)
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium block mb-2">Select CSV File</label>
                <Input 
                  type="file" 
                  accept=".csv"
                  onChange={(e) => setCSVFile(e.target.files[0])}
                  required
                />
                {csvFile && (
                  <p className="text-sm text-green-600 mt-1">✓ {csvFile.name} selected</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowCSVUploadDialog(false);
                  setCSVFile(null);
                  setCSVUploadTarget({ chapter_id: '', test_ids: [] });
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !selectedChapter}
              >
                {loading ? 'Uploading...' : 'Upload Questions'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TestManagement;
